import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  fetchHandshakeCanaryStatus,
  getHandshakeCanaryQueryKey,
  isHandshakeCanaryChecking,
  isHandshakeCanaryHealthy,
  isHandshakeCanaryUnavailable,
  type HandshakeCanaryStatus,
  useHandshakeCanaryStatus,
} from '@/common/hooks/oralExam/useHandshakeCanaryStatus';

export const DRY_RUN_UNAVAILABLE_MESSAGE = 'Sorry, the dry run is currently not available.';

const CANARY_CONFIRM_TIMEOUT_MS = 60_000;
const CANARY_CONFIRM_POLL_MS = 2_000;

type ConfirmDryRunCanaryOptions = {
  enabled: boolean;
  gatesStart: boolean;
  fetchStatus: (options?: { refresh?: boolean }) => Promise<HandshakeCanaryStatus>;
  onStatus?: (status: HandshakeCanaryStatus) => void;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
};

/**
 * Re-read the handshake canary at the moment the student starts.
 * Healthy last verdict proceeds immediately; stale/in-flight/unhealthy
 * kicks a refresh and waits until healthy or the timeout.
 */
export async function confirmDryRunCanary({
  enabled,
  gatesStart,
  fetchStatus,
  onStatus,
  now = Date.now,
  sleep = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
  }),
}: ConfirmDryRunCanaryOptions): Promise<boolean> {
  if (!enabled || !gatesStart) return true;
  let status = await fetchStatus();
  onStatus?.(status);
  if (isHandshakeCanaryHealthy(status)) return true;
  status = await fetchStatus({ refresh: true });
  onStatus?.(status);
  const deadline = now() + CANARY_CONFIRM_TIMEOUT_MS;
  while (now() < deadline && isHandshakeCanaryChecking(status, { isFetched: true })) {
    // Sequential canary poll — each wait must finish before the next fetch.
    // eslint-disable-next-line no-await-in-loop
    await sleep(CANARY_CONFIRM_POLL_MS);
    // eslint-disable-next-line no-await-in-loop
    status = await fetchStatus();
    onStatus?.(status);
  }
  return isHandshakeCanaryHealthy(status);
}

/**
 * Fail-closed AI-examiner gate for student dry-run start.
 * Same handshake-canary verdict the lecturer StartGate uses.
 */
export function useDryRunExaminerGate(enabled: boolean) {
  const queryClient = useQueryClient();
  const {
    data,
    isFetched,
    isError,
    refetchWithRefresh,
  } = useHandshakeCanaryStatus(enabled);
  const gatesStart = data?.gatesStart !== false;
  const checking = enabled && gatesStart && isHandshakeCanaryChecking(data, {
    isFetched,
    isError,
  });
  const unavailable = enabled && gatesStart && isHandshakeCanaryUnavailable(data, {
    isFetched,
    isError,
  });
  const canStart = !enabled
    || !gatesStart
    || isHandshakeCanaryHealthy(data);

  const confirmBeforeStart = useCallback(async () => {
    if (!enabled || !gatesStart) return true;
    if (isHandshakeCanaryHealthy(data)) return true;
    return confirmDryRunCanary({
      enabled,
      gatesStart,
      fetchStatus: fetchHandshakeCanaryStatus,
      onStatus: (status) => {
        queryClient.setQueryData(getHandshakeCanaryQueryKey(), status);
      },
    });
  }, [data, enabled, gatesStart, queryClient]);

  return {
    checking,
    unavailable,
    canStart,
    refetchWithRefresh,
    confirmBeforeStart,
  };
}
